import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type { AiConfigApiClient } from '../api/client'
import type {
  Agent,
  AgentBranchAssignment,
  AgentConfiguration,
  AgentSipRegistrationStatus,
  Branch,
  BranchDataFieldAssignment,
  CreateAgentInput,
  CreateBranchInput,
  CreateDataFieldInput,
  DataField,
  PaginatedResponse,
  PatchAgentInput,
  PublishRequest,
  PublishResponse,
  ScrapeJob,
  SipRegistrationState,
  StandardDataField,
  StartScrapeJobInput,
  StartScrapeJobResponse,
  Tag,
  TestCall,
  UpdateAgentInput,
  UpdateBranchInput,
  UpdateDataFieldInput,
  Voice,
  VoicePreview,
  VoicePreviewInput,
} from '../types'
import { unwrapResponse } from './response'

export { AiConfigValidationError, type AiConfigRequestError } from './response'

export const AI_CONFIG_KEYS = {
  all: ['ai-config'] as const,
  agents: () => [...AI_CONFIG_KEYS.all, 'agents'] as const,
  agentList: (params: Record<string, unknown>) =>
    [...AI_CONFIG_KEYS.agents(), 'list', params] as const,
  agentDetail: (id: string) => [...AI_CONFIG_KEYS.agents(), id] as const,
  agentBranches: (id: string) =>
    [...AI_CONFIG_KEYS.agents(), id, 'branches'] as const,
  agentConfiguration: (id: string) =>
    [...AI_CONFIG_KEYS.agents(), id, 'configuration'] as const,
  agentSipRegistrationStatus: (id: string) =>
    [...AI_CONFIG_KEYS.agents(), id, 'sip-registration-status'] as const,
  branches: () => [...AI_CONFIG_KEYS.all, 'branches'] as const,
  branchList: (params: Record<string, unknown>) =>
    [...AI_CONFIG_KEYS.branches(), 'list', params] as const,
  branchDetail: (id: string) => [...AI_CONFIG_KEYS.branches(), id] as const,
  branchVersions: (name: string) =>
    [...AI_CONFIG_KEYS.branches(), 'versions', name] as const,
  branchDataFields: (id: string) =>
    [...AI_CONFIG_KEYS.branches(), id, 'data-fields'] as const,
  branchAgentUsage: (branchId: string) =>
    [...AI_CONFIG_KEYS.branches(), branchId, 'agent-usage'] as const,
  dataFields: () => [...AI_CONFIG_KEYS.all, 'data-fields'] as const,
  dataFieldBranchUsage: (dataFieldId: string) =>
    [...AI_CONFIG_KEYS.dataFields(), dataFieldId, 'branch-usage'] as const,
  dfList: (params: Record<string, unknown>) =>
    [...AI_CONFIG_KEYS.dataFields(), 'list', params] as const,
  dfDetail: (id: string) => [...AI_CONFIG_KEYS.dataFields(), id] as const,
  dfVersions: (name: string) =>
    [...AI_CONFIG_KEYS.dataFields(), 'versions', name] as const,
  tags: () => [...AI_CONFIG_KEYS.all, 'tags'] as const,
  voices: () => [...AI_CONFIG_KEYS.all, 'voices'] as const,
  // NB: intentionally NOT under dataFields() — the standard catalogue is static,
  // and nesting it under the DF prefix would refetch it on every custom-DF CRUD
  // op (which invalidates dataFields()).
  standardDataFields: () =>
    [...AI_CONFIG_KEYS.all, 'standard-data-fields'] as const,
  scrapeJob: (id: string) =>
    [...AI_CONFIG_KEYS.all, 'scrape-jobs', id] as const,
  agentTestCalls: (agentId: string) =>
    [...AI_CONFIG_KEYS.agents(), agentId, 'test-calls'] as const,
  testCallDetail: (id: string) =>
    [...AI_CONFIG_KEYS.all, 'test-calls', id] as const,
}

const AI_CONFIG_STALE_MS = 30 * 1000

// ── Agent Queries ──

export const fetchAgentCount = async (
  api: AiConfigApiClient,
  params: Record<string, unknown>,
): Promise<number> => {
  const response = await api.transport.get<PaginatedResponse<Agent>>(
    '/agents',
    { ...params, limit: 1 },
  )
  if (!response.ok) return 0
  return (response.data as PaginatedResponse<Agent>)?.pagination?.total ?? 0
}

export const useListAgents = (
  api: AiConfigApiClient,
  params: Record<string, unknown> = {},
  options: { enabled?: boolean } = {},
) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.agentList(params),
    queryFn: async () => {
      const response = await api.transport.get<PaginatedResponse<Agent>>(
        '/agents',
        params,
      )
      return unwrapResponse(response)
    },
    staleTime: AI_CONFIG_STALE_MS,
    ...(options.enabled !== undefined ? { enabled: options.enabled } : {}),
  })

  return {
    ...query,
    agents: query.data?.results ?? [],
    pagination: query.data?.pagination,
  }
}

const PHONE_AVAILABILITY_STALE_MS = 10_000

export const useCheckSignalsPhoneAvailability = (
  api: AiConfigApiClient,
  phoneNumber: string,
  options: { enabled?: boolean; excludeAgentId?: string } = {},
) => {
  const trimmed = phoneNumber.trim()
  const enabled = (options.enabled ?? true) && trimmed.length > 0

  const query = useQuery({
    queryKey: [
      ...AI_CONFIG_KEYS.all,
      'signals-phone-availability',
      trimmed,
      options.excludeAgentId ?? null,
    ],
    queryFn: async () => {
      const limit = options.excludeAgentId ? 2 : 1
      const response = await api.transport.get<PaginatedResponse<Agent>>(
        '/agents',
        // Fetch 2 when editing so we can skip past a self-match and still see a conflict.
        { 'signals-ai-phone-number': trimmed, limit },
      )
      const data = unwrapResponse(response)
      const conflict = data.results.find(
        (agent) => agent.id !== options.excludeAgentId,
      )
      return { available: !conflict, conflictAgent: conflict ?? null }
    },
    enabled,
    staleTime: PHONE_AVAILABILITY_STALE_MS,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    isAvailable: query.data?.available ?? null,
    conflictAgent: query.data?.conflictAgent ?? null,
  }
}

export const useGetAgent = (api: AiConfigApiClient, id: string) => {
  return useQuery({
    queryKey: AI_CONFIG_KEYS.agentDetail(id),
    queryFn: async () => {
      const response = await api.transport.get<{ data: Agent }>(`/agents/${id}`)
      return unwrapResponse(response).data
    },
    enabled: !!id,
    staleTime: AI_CONFIG_STALE_MS,
  })
}

const AGENT_BRANCHES_STALE_MS = 60 * 1000

export const useGetAgentBranches = (
  api: AiConfigApiClient,
  agentId: string,
) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.agentBranches(agentId),
    queryFn: async () => {
      const response = await api.transport.get<
        PaginatedResponse<AgentBranchAssignment>
      >(`/agents/${agentId}/branches`)
      return unwrapResponse(response)
    },
    enabled: !!agentId,
    staleTime: AGENT_BRANCHES_STALE_MS,
  })

  return {
    ...query,
    assignments: query.data?.results ?? [],
  }
}

/**
 * Click-time read for surfaces outside React Query (e.g. the agents list's
 * wizard-resume check): the list response carries no publish signal, so the
 * only way to know an agent never published is `externalAgentId` here.
 */
export const fetchAgentConfiguration = async (
  api: AiConfigApiClient,
  id: string,
): Promise<AgentConfiguration> => {
  const response = await api.transport.get<AgentConfiguration>(
    `/agents/${id}/configuration`,
  )
  return unwrapResponse(response)
}

export const useGetAgentConfiguration = (
  api: AiConfigApiClient,
  id: string,
) => {
  return useQuery({
    queryKey: AI_CONFIG_KEYS.agentConfiguration(id),
    queryFn: () => fetchAgentConfiguration(api, id),
    enabled: !!id,
    staleTime: AI_CONFIG_STALE_MS,
  })
}

const REGISTERED_STATES = new Set(['registered', 'active', 'online', 'up'])
const UNREGISTERED_STATES = new Set([
  'unregistered',
  'inactive',
  'offline',
  'down',
  'expired',
  'failed',
])

/**
 * Read `GET /agents/{id}/sip-registration-status` (see
 * `AgentSipRegistrationStatus` for the confirmed payload).
 *
 * `registered` is the authoritative field — it is a boolean, so it cannot be
 * ambiguous — and `status` is only consulted when the boolean is missing, which
 * would mean the contract moved. An unrecognised state is kept verbatim rather
 * than flattened, so a payload change shows up in the cell instead of silently
 * reading as "not registered".
 */
export const toSipRegistrationStatus = (
  payload: unknown,
): AgentSipRegistrationStatus => {
  if (!payload || typeof payload !== 'object') return { state: 'unknown' }
  const body = payload as Record<string, unknown>

  const details = {
    ...(typeof body.connected === 'boolean'
      ? { connected: body.connected }
      : {}),
    ...(typeof body.expiresAt === 'string'
      ? { expiresAt: body.expiresAt }
      : {}),
    ...(typeof body.lastResponse === 'string'
      ? { lastResponse: body.lastResponse }
      : {}),
    ...(typeof body.failures === 'number' ? { failures: body.failures } : {}),
  }

  if (typeof body.registered === 'boolean') {
    return {
      state: body.registered ? 'registered' : 'unregistered',
      ...details,
    }
  }

  const raw = typeof body.status === 'string' ? body.status.trim() : ''
  if (!raw) return { state: 'unknown', ...details }

  const normalized = raw.toLowerCase()
  const state: SipRegistrationState = REGISTERED_STATES.has(normalized)
    ? 'registered'
    : UNREGISTERED_STATES.has(normalized)
      ? 'unregistered'
      : 'unknown'
  return { state, raw, ...details }
}

/**
 * Per agent, so the list page batches these itself (`useQueries`) rather than
 * this exposing a hook it would have to call in a loop.
 */
export const fetchAgentSipRegistrationStatus = async (
  api: AiConfigApiClient,
  id: string,
): Promise<AgentSipRegistrationStatus> => {
  const response = await api.transport.get<unknown>(
    `/agents/${id}/sip-registration-status`,
  )
  return toSipRegistrationStatus(unwrapResponse(response))
}

// ── Branch Queries ──

export const useListBranches = (
  api: AiConfigApiClient,
  params: Record<string, unknown> = {},
) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.branchList(params),
    queryFn: async () => {
      const response = await api.transport.get<PaginatedResponse<Branch>>(
        '/branches',
        params,
      )
      return unwrapResponse(response)
    },
    staleTime: AI_CONFIG_STALE_MS,
  })

  return {
    ...query,
    branches: query.data?.results ?? [],
    pagination: query.data?.pagination,
  }
}

export const useGetBranch = (api: AiConfigApiClient, id: string) => {
  return useQuery({
    queryKey: AI_CONFIG_KEYS.branchDetail(id),
    queryFn: async () => {
      const response = await api.transport.get<Branch>(`/branches/${id}`)
      return unwrapResponse(response)
    },
    enabled: !!id,
    staleTime: AI_CONFIG_STALE_MS,
  })
}

/**
 * Prefetches branch details for a set of ids in parallel, deduped, into the
 * same cache slot useGetBranch reads. Lets parent components (e.g. list pages
 * with per-row ancestor links) collapse N sequential per-row queries into one
 * fan-out, while leaf components keep using useGetBranch as cache hits.
 */
export const usePrefetchBranches = (api: AiConfigApiClient, ids: string[]) => {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)))
  return useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: AI_CONFIG_KEYS.branchDetail(id),
      queryFn: async () => {
        const response = await api.transport.get<Branch>(`/branches/${id}`)
        return unwrapResponse(response)
      },
      staleTime: AI_CONFIG_STALE_MS,
    })),
  })
}

export const useGetBranchVersions = (api: AiConfigApiClient, name: string) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.branchVersions(name),
    queryFn: async () => {
      const response = await api.transport.get<PaginatedResponse<Branch>>(
        '/branches',
        { name, limit: 100 },
      )
      return unwrapResponse(response)
    },
    enabled: !!name,
    staleTime: AI_CONFIG_STALE_MS,
  })

  return {
    ...query,
    versions: query.data?.results ?? [],
  }
}

export const useGetBranchDataFields = (
  api: AiConfigApiClient,
  branchId: string,
) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.branchDataFields(branchId),
    queryFn: async () => {
      const response = await api.transport.get<
        PaginatedResponse<BranchDataFieldAssignment>
      >(`/branches/${branchId}/data-fields`)
      return unwrapResponse(response)
    },
    enabled: !!branchId,
    staleTime: AI_CONFIG_STALE_MS,
  })

  return {
    ...query,
    assignments: query.data?.results ?? [],
  }
}

// ── Data Field Queries ──

export const useListDataFields = (
  api: AiConfigApiClient,
  params: Record<string, unknown> = {},
) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.dfList(params),
    queryFn: async () => {
      const response = await api.transport.get<PaginatedResponse<DataField>>(
        '/data-fields',
        params,
      )
      return unwrapResponse(response)
    },
    staleTime: AI_CONFIG_STALE_MS,
  })

  return {
    ...query,
    dataFields: query.data?.results ?? [],
    pagination: query.data?.pagination,
  }
}

export const useGetDataField = (api: AiConfigApiClient, id: string) => {
  return useQuery({
    queryKey: AI_CONFIG_KEYS.dfDetail(id),
    queryFn: async () => {
      const response = await api.transport.get<DataField>(`/data-fields/${id}`)
      return unwrapResponse(response)
    },
    enabled: !!id,
    staleTime: AI_CONFIG_STALE_MS,
  })
}

/**
 * Prefetches data field details for a set of ids in parallel, deduped, into
 * the same cache slot useGetDataField reads. Mirror of usePrefetchBranches —
 * see that hook for the ancestor-link N+1 motivation.
 */
export const usePrefetchDataFields = (
  api: AiConfigApiClient,
  ids: string[],
) => {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)))
  return useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: AI_CONFIG_KEYS.dfDetail(id),
      queryFn: async () => {
        const response = await api.transport.get<DataField>(
          `/data-fields/${id}`,
        )
        return unwrapResponse(response)
      },
      staleTime: AI_CONFIG_STALE_MS,
    })),
  })
}

export const useGetDataFieldVersions = (
  api: AiConfigApiClient,
  name: string,
) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.dfVersions(name),
    queryFn: async () => {
      const response = await api.transport.get<PaginatedResponse<DataField>>(
        '/data-fields',
        { name, limit: 100 },
      )
      return unwrapResponse(response)
    },
    enabled: !!name,
    staleTime: AI_CONFIG_STALE_MS,
  })

  return {
    ...query,
    versions: query.data?.results ?? [],
  }
}

/**
 * Prefetches data field version lists for a set of names in parallel,
 * deduped, into the same cache slot useGetDataFieldVersions reads. Use
 * at the page level to collapse N+1 rows each firing their own versions
 * query for the "vN available" nudge.
 */
export const usePrefetchDataFieldVersions = (
  api: AiConfigApiClient,
  names: string[],
) => {
  const uniqueNames = Array.from(new Set(names.filter(Boolean)))
  return useQueries({
    queries: uniqueNames.map((name) => ({
      queryKey: AI_CONFIG_KEYS.dfVersions(name),
      queryFn: async () => {
        const response = await api.transport.get<PaginatedResponse<DataField>>(
          '/data-fields',
          { name, limit: 100 },
        )
        return unwrapResponse(response)
      },
      staleTime: AI_CONFIG_STALE_MS,
    })),
  })
}

// ── Reverse Lookup Queries ──

const BRANCH_AGENT_USAGE_STALE_MS = 5 * 60 * 1000
const DATA_FIELD_BRANCH_USAGE_STALE_MS = 5 * 60 * 1000
const DEFAULT_USAGE_PAGE_SIZE = 10

export interface BranchAgentUsageParams {
  limit?: number
  offset?: number
}

export const fetchBranchAgentCount = async (
  api: AiConfigApiClient,
  branchId: string,
): Promise<number> => {
  const response = await api.transport.get<
    PaginatedResponse<{ agent: Agent; assignment: AgentBranchAssignment }>
  >(`/branches/${branchId}/agents`, { limit: 1 })
  // Throw on transport failure so react-query surfaces an error state and
  // retries, rather than stamping a "0 agents" cell that hides the failure
  // until manual refetch.
  if (!response.ok) {
    throw new Error(`Failed to fetch agent count for branch ${branchId}`)
  }
  return (
    (
      response.data as PaginatedResponse<{
        agent: Agent
        assignment: AgentBranchAssignment
      }>
    )?.pagination?.total ?? 0
  )
}

export const useBranchAgentUsage = (
  api: AiConfigApiClient,
  branchId: string,
  params: BranchAgentUsageParams = {},
) => {
  const limit = params.limit ?? DEFAULT_USAGE_PAGE_SIZE
  const offset = params.offset ?? 0

  const query = useQuery({
    queryKey: [...AI_CONFIG_KEYS.branchAgentUsage(branchId), { limit, offset }],
    queryFn: async () => {
      const response = await api.transport.get<
        PaginatedResponse<{ agent: Agent; assignment: AgentBranchAssignment }>
      >(`/branches/${branchId}/agents`, { limit, offset })
      return unwrapResponse(response)
    },
    enabled: !!branchId,
    staleTime: BRANCH_AGENT_USAGE_STALE_MS,
  })

  return {
    ...query,
    usages: query.data?.results ?? [],
    pagination: query.data?.pagination,
  }
}

export interface DataFieldBranchUsageParams {
  limit?: number
  offset?: number
}

export const useDataFieldBranchUsage = (
  api: AiConfigApiClient,
  dataFieldId: string,
  params: DataFieldBranchUsageParams = {},
) => {
  const limit = params.limit ?? DEFAULT_USAGE_PAGE_SIZE
  const offset = params.offset ?? 0

  const query = useQuery({
    queryKey: [
      ...AI_CONFIG_KEYS.dataFieldBranchUsage(dataFieldId),
      { limit, offset },
    ],
    queryFn: async () => {
      const response = await api.transport.get<
        PaginatedResponse<{
          branch: Branch
          assignment: BranchDataFieldAssignment
        }>
      >(`/data-fields/${dataFieldId}/branches`, { limit, offset })
      return unwrapResponse(response)
    },
    enabled: !!dataFieldId,
    staleTime: DATA_FIELD_BRANCH_USAGE_STALE_MS,
  })

  return {
    ...query,
    usages: query.data?.results ?? [],
    pagination: query.data?.pagination,
  }
}

// ── Agent Mutations ──

export const useCreateAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateAgentInput) => {
      const response = await api.transport.post<{ data: Agent }>(
        '/agents',
        data,
      )
      return unwrapResponse(response).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
    },
  })
}

export const useUpdateAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateAgentInput) => {
      const response = await api.transport.put<{ message: string }>(
        `/agents/${id}`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentDetail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
    },
  })
}

// Incremental step-save for the wizard (AI-142). The API has no
// PATCH /agents/{id} — the spec's update route is PUT — so step slices ride
// PUT; the mock merges shallowly so a slice still only replaces the fields
// it carries.
export const usePatchAgentStep = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: PatchAgentInput) => {
      const response = await api.transport.put<{ data: Agent }>(
        `/agents/${id}`,
        data,
      )
      return unwrapResponse(response).data
    },
    onSuccess: (data, variables) => {
      // Seed the cache from the response before invalidating. The wizard
      // rebuilds officeData read-modify-write on every step save, and that blob
      // replaces wholesale — so a step whose Next fires before the previous
      // step's refetch lands would merge over a stale blob and drop the field
      // its predecessor just saved. invalidate alone only schedules a refetch.
      //
      // This belongs here rather than on useUpdateAgent: the wizard saves
      // through THIS hook, and this mutationFn already unwraps to a bare Agent,
      // which is the shape useGetAgent stores. useUpdateAgent returns the
      // envelope, so seeding from there would poison the detail cache for the
      // edit/detail pages that read it.
      if (data) {
        queryClient.setQueryData(AI_CONFIG_KEYS.agentDetail(variables.id), data)
      }
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentDetail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
    },
  })
}

export const useDeleteAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.transport.delete<{ message: string }>(
        `/agents/${id}`,
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
    },
  })
}

// ── Branch Mutations ──

export const useCreateBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateBranchInput) => {
      const response = await api.transport.post<Branch>('/branches', data)
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.branches() })
    },
  })
}

export const useUpdateBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateBranchInput) => {
      const response = await api.transport.put<{ message: string }>(
        `/branches/${id}`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      // branches() is a prefix; invalidating it covers branchDetail,
      // branchList, branchVersions, branchDataFields, and every sibling's
      // branchAgentUsage in one shot. The explicit branchDetail call
      // below is redundant but kept for clarity at the mutation site.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchDetail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.branches() })
      // Activating a branch cascades server-side: every prior active
      // sibling in the same lineage gets deprecated AND every agent
      // pointing at those siblings is silently reassigned to this branch.
      // Broad-invalidate agents() so every agent detail / agentBranches /
      // agentConfiguration refetches. branches() above already covered
      // sibling branchAgentUsage via prefix match.
      if (variables.status === 'active') {
        queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
      }
    },
  })
}

export const useCopyBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: string | { id: string; detach?: boolean }) => {
      const id = typeof input === 'string' ? input : input.id
      const detach = typeof input === 'string' ? false : input.detach
      const response = await api.transport.post<Branch>(
        `/branches/${id}/copy`,
        detach ? { detach: true } : undefined,
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.branches() })
    },
  })
}

/**
 * Title-only rename via `PUT /branches/{id}/rename`. Unlike
 * `useUpdateBranch` (full-replace PUT to `/branches/{id}`, gated to
 * draft-status content updates), the dedicated rename endpoint accepts
 * `{ title }` alone and bypasses the draft restriction — server-side
 * the repo's `RenameBranch` does no status check.
 *
 * The frontend uses this for the admin/superuser pencil-edit on the
 * branch title in `EntityDetailHeaderCard`, where edits to active or
 * deprecated versions are an explicit override. Sends only the title
 * so non-title content fields aren't echoed and accidentally rewritten.
 */
export const useRenameBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const response = await api.transport.put<{ message: string }>(
        `/branches/${id}/rename`,
        { title },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchDetail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.branches() })
    },
  })
}

export const useDeleteBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.transport.delete<{ message: string }>(
        `/branches/${id}`,
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.branches() })
    },
  })
}

// ── Data Field Mutations ──

export const useCreateDataField = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateDataFieldInput) => {
      const response = await api.transport.post<DataField>('/data-fields', data)
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.dataFields() })
    },
  })
}

export const useUpdateDataField = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateDataFieldInput) => {
      const response = await api.transport.put<{ message: string }>(
        `/data-fields/${id}`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.dfDetail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.dataFields() })
    },
  })
}

export const useCopyDataField = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: string | { id: string; detach?: boolean }) => {
      const id = typeof input === 'string' ? input : input.id
      const detach = typeof input === 'string' ? false : input.detach
      const response = await api.transport.post<DataField>(
        `/data-fields/${id}/copy`,
        detach ? { detach: true } : undefined,
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.dataFields() })
    },
  })
}

/**
 * Title-only rename via `PUT /data-fields/{id}/rename`. Mirror of
 * `useRenameBranch` — accepts `{ title }`, bypasses the draft-only
 * restriction the full-replace `useUpdateDataField` enforces, and
 * sends only the title so no other content fields get echoed back.
 *
 * Used by the admin/superuser pencil-edit on the data field display
 * label, where the override applies to a single version only (the
 * rest of the variant is unaffected).
 */
export const useRenameDataField = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const response = await api.transport.put<{ message: string }>(
        `/data-fields/${id}/rename`,
        { title },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.dfDetail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.dataFields() })
    },
  })
}

export const useReassignDataFields = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      oldDataFieldId,
      newDataFieldId,
      branchIds,
    }: {
      oldDataFieldId: string
      newDataFieldId: string
      branchIds: string[]
    }) => {
      const response = await api.transport.post<{ message: string }>(
        '/data-fields/reassign',
        { oldDataFieldId, newDataFieldId, branchIds },
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.branches() })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.dataFields(),
      })
    },
  })
}

export const useDeleteDataField = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.transport.delete<{ message: string }>(
        `/data-fields/${id}`,
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.dataFields() })
    },
  })
}

// ── Assignment Mutations ──

export const useAssignBranchToAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      ...data
    }: {
      agentId: string
      branchId: string
      order: number
      enabled: boolean
    }) => {
      const response = await api.transport.post<{ message: string }>(
        `/agents/${agentId}/branches`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentBranches(variables.agentId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchAgentUsage(variables.branchId),
      })
      // Compiled view depends on the assignment set — stale "Config Valid"
      // badge without this.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(variables.agentId),
      })
    },
  })
}

export const useUpdateAgentBranchAssignment = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      branchId,
      // skipInvalidate is a client-only flag — pull it out of the body.
      skipInvalidate: _skipInvalidate,
      ...data
    }: {
      agentId: string
      branchId: string
      order?: number
      enabled?: boolean
      /**
       * When true, don't invalidate list/usage queries on success. Useful
       * for batch reorders where the caller wants a single invalidation
       * after the final PUT instead of a refetch storm per step.
       */
      skipInvalidate?: boolean
    }) => {
      const response = await api.transport.put<{ message: string }>(
        `/agents/${agentId}/branches/${branchId}`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      if (variables.skipInvalidate) return
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentBranches(variables.agentId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchAgentUsage(variables.branchId),
      })
      // Reorder / enable-disable changes the compiled view.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(variables.agentId),
      })
    },
  })
}

export const useReorderAgentBranches = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      branchIds,
    }: {
      agentId: string
      branchIds: string[]
    }) => {
      const response = await api.transport.put<{ message: string }>(
        `/agents/${agentId}/branches/order`,
        { branchIds },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentBranches(variables.agentId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(variables.agentId),
      })
    },
  })
}

export const useReassignAgentBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      branchId,
      newBranchId,
    }: {
      agentId: string
      branchId: string
      newBranchId: string
    }) => {
      const response = await api.transport.put<{ message: string }>(
        `/agents/${agentId}/branches/${branchId}/reassign`,
        { branchId: newBranchId },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentBranches(variables.agentId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchAgentUsage(variables.branchId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchAgentUsage(variables.newBranchId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(variables.agentId),
      })
    },
  })
}

export const useUnassignBranchFromAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      branchId,
    }: {
      agentId: string
      branchId: string
    }) => {
      const response = await api.transport.delete<{ message: string }>(
        `/agents/${agentId}/branches/${branchId}`,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentBranches(variables.agentId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchAgentUsage(variables.branchId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(variables.agentId),
      })
    },
  })
}

export const useCloneBranchAssignments = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      sourceAgentId,
    }: {
      agentId: string
      sourceAgentId: string
    }) => {
      const response = await api.transport.post<{ message: string }>(
        `/agents/${agentId}/branches/clone`,
        { sourceAgentId },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentBranches(variables.agentId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branches(),
        predicate: (query) => query.queryKey.includes('agent-usage'),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(variables.agentId),
      })
    },
  })
}

export const useAssignDataFieldToBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      branchId,
      ...data
    }: {
      branchId: string
      dataFieldId: string
      order: number
      enabled: boolean
    }) => {
      const response = await api.transport.post<{ message: string }>(
        `/branches/${branchId}/data-fields`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchDataFields(variables.branchId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.dataFieldBranchUsage(variables.dataFieldId),
      })
      // Any agent whose assignment includes this branch has a stale
      // compiled view. We don't track agents-using-branch in cache, so
      // predicate-invalidate all agentConfiguration entries.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agents(),
        predicate: (query) => query.queryKey.includes('configuration'),
      })
    },
  })
}

export const useUpdateBranchDfAssignment = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      branchId,
      dataFieldId,
      // skipInvalidate is a client-only flag — pull it out of the body.
      skipInvalidate: _skipInvalidate,
      ...data
    }: {
      branchId: string
      dataFieldId: string
      order?: number
      enabled?: boolean
      /**
       * When true, don't invalidate list/usage queries on success. Useful
       * for batch reorders where the caller wants a single invalidation
       * after the final PUT instead of a refetch storm per step.
       */
      skipInvalidate?: boolean
    }) => {
      const response = await api.transport.put<{ message: string }>(
        `/branches/${branchId}/data-fields/${dataFieldId}`,
        data,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      if (variables.skipInvalidate) return
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchDataFields(variables.branchId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.dataFieldBranchUsage(variables.dataFieldId),
      })
      // Reorder / toggle-enabled on a branch's DF set changes the
      // compiled view of every agent that uses the branch.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agents(),
        predicate: (query) => query.queryKey.includes('configuration'),
      })
    },
  })
}

export const useReorderBranchDataFields = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      branchId,
      dataFieldIds,
    }: {
      branchId: string
      dataFieldIds: string[]
    }) => {
      const response = await api.transport.put<{ message: string }>(
        `/branches/${branchId}/data-fields/order`,
        { dataFieldIds },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchDataFields(variables.branchId),
      })
      // Reorder on a branch's DF set changes the compiled view of every
      // agent that uses the branch.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agents(),
        predicate: (query) => query.queryKey.includes('configuration'),
      })
    },
  })
}

export const useUnassignDataFieldFromBranch = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      branchId,
      dataFieldId,
    }: {
      branchId: string
      dataFieldId: string
    }) => {
      const response = await api.transport.delete<{ message: string }>(
        `/branches/${branchId}/data-fields/${dataFieldId}`,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.branchDataFields(variables.branchId),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.dataFieldBranchUsage(variables.dataFieldId),
      })
      // Unassigning a DF from a branch changes the compiled view of
      // every agent that uses the branch.
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agents(),
        predicate: (query) => query.queryKey.includes('configuration'),
      })
    },
  })
}

// ── Tag Queries & Mutations ──

export const useListTags = (api: AiConfigApiClient) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.tags(),
    queryFn: async () => {
      const response = await api.transport.get<{ results: Tag[] }>('/tags')
      return unwrapResponse(response)
    },
    staleTime: AI_CONFIG_STALE_MS,
  })
  return {
    ...query,
    tags: query.data?.results ?? [],
  }
}

export const useCreateTag = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (name: string) => {
      const response = await api.transport.post<{ data: Tag }>('/tags', {
        name,
      })
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.tags() })
    },
  })
}

export const useDeleteTag = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.transport.delete<{ message: string }>(
        `/tags/${id}`,
      )
      return unwrapResponse(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.tags() })
    },
  })
}

// ── Voices & standard data fields (AI-170 publish catalogues) ──

export const useListVoices = (api: AiConfigApiClient) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.voices(),
    queryFn: async () => {
      const response = await api.transport.get<{ results: Voice[] }>('/voices')
      return unwrapResponse(response)
    },
    staleTime: AI_CONFIG_STALE_MS,
  })
  return {
    ...query,
    voices: query.data?.results ?? [],
  }
}

export const useListStandardDataFields = (api: AiConfigApiClient) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.standardDataFields(),
    queryFn: async () => {
      const response = await api.transport.get<{
        results: StandardDataField[]
      }>('/data-fields/standard')
      return unwrapResponse(response)
    },
    staleTime: AI_CONFIG_STALE_MS,
  })
  return {
    ...query,
    standardDataFields: query.data?.results ?? [],
  }
}

// Pure command: preview a voice; returns base64 audio + its content type.
export const usePreviewVoice = (api: AiConfigApiClient) => {
  return useMutation({
    mutationFn: async (input: VoicePreviewInput) => {
      const response = await api.transport.post<VoicePreview>(
        '/voices/preview',
        input,
      )
      return unwrapResponse(response)
    },
  })
}

// ── Publish / Activate (AI-170) ──

// Publish is the slowest call this client makes — the host clones an assistant
// and creates a SIP connection on a first publish. Generous rather than tuned:
// a client abort does not stop the server, so timing out early reports a
// failure for work that is still going to land.
const PUBLISH_TIMEOUT_MS = 60_000

// POST /agents/{id}/publish. promote:false stages; promote:true goes live.
// Build the body with buildPublishRequest so deterministic-off toggles are
// resent. Returns the AgentDefinition the host now holds, plus any warnings.
export const usePublishAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & PublishRequest) => {
      const response = await api.transport.post<PublishResponse>(
        `/agents/${id}/publish`,
        body,
        // The shared transport times out at 10s, and publish routinely takes
        // longer: it reads through to the host, and a FIRST publish clones the
        // assistant and creates the SIP connection. Measured 7.5s for a repeat
        // publish and >10s for a first one, so the first attempt aborted and
        // the retry — cheaper, because the clone already existed — succeeded.
        // That is the "first Activate fails, second works" report.
        //
        // The abort is client-side only: the server keeps going and finishes
        // provisioning, so the failure was cosmetic and the retry was really a
        // republish. Worth knowing, because it means the error was lying.
        { timeout: PUBLISH_TIMEOUT_MS },
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentDetail(id),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
    },
  })
}

export const useUnpublishAgent = (api: AiConfigApiClient) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.transport.delete<{ message: string }>(
        `/agents/${id}/publish`,
      )
      return unwrapResponse(response)
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentDetail(id),
      })
      queryClient.invalidateQueries({
        queryKey: AI_CONFIG_KEYS.agentConfiguration(id),
      })
      queryClient.invalidateQueries({ queryKey: AI_CONFIG_KEYS.agents() })
    },
  })
}

// ── Scrape Job Mutations & Queries ──

export const useStartScrapeJob = (api: AiConfigApiClient) => {
  return useMutation({
    mutationFn: async (data: StartScrapeJobInput) => {
      const response = await api.transport.post<StartScrapeJobResponse>(
        '/scrape-jobs',
        data,
      )
      return unwrapResponse(response)
    },
  })
}

export const useGetScrapeJob = (
  api: AiConfigApiClient,
  jobId: string,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) => {
  return useQuery({
    queryKey: AI_CONFIG_KEYS.scrapeJob(jobId),
    queryFn: async () => {
      // jobId is always from the server's own POST response — never user input
      const response = await api.transport.get<ScrapeJob>(
        `/scrape-jobs/${jobId}`,
      )
      return unwrapResponse(response)
    },
    enabled: !!jobId && (options.enabled ?? true),
    refetchInterval: options.refetchInterval,
  })
}

// ── Test Calls (AI-154) ──

export const useListTestCalls = (api: AiConfigApiClient, agentId: string) => {
  const query = useQuery({
    queryKey: AI_CONFIG_KEYS.agentTestCalls(agentId),
    queryFn: async () => {
      const response = await api.transport.get<PaginatedResponse<TestCall>>(
        `/agents/${agentId}/test-calls`,
      )
      return unwrapResponse(response)
    },
    enabled: !!agentId,
    staleTime: AI_CONFIG_STALE_MS,
  })

  return {
    ...query,
    testCalls: query.data?.results ?? [],
  }
}

export const useGetTestCall = (api: AiConfigApiClient, id: string) => {
  return useQuery({
    queryKey: AI_CONFIG_KEYS.testCallDetail(id),
    queryFn: async () => {
      const response = await api.transport.get<{ data: TestCall }>(
        `/test-calls/${id}`,
      )
      return unwrapResponse(response).data
    },
    enabled: !!id,
    staleTime: AI_CONFIG_STALE_MS,
  })
}
